import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Network } from "@capacitor/network";
import { SyncEmployeeDataAPI } from "../Admin/Redux/employeeAttendance";
import { userStorage } from "./userStorage";
import { attendanceQueueManager } from "./attendanceQueueManager";
import { useSocket } from "../contexts/SocketContext";
import { serverHealthService } from "./serverHealthService";

export const PAGE_LIMIT = 60;
const MAX_RETRIES = 5;

export const useEmployeeSync = () => {
  const dispatch = useDispatch();
  const { socket, isConnected } = useSocket();
  const isConnectedRef = useRef(isConnected);

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  const [syncProgress, setSyncProgress] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [triggerCount, setTriggerCount] = useState({ count: 0, isManual: false });

  const triggerSync = useCallback((isManual: boolean = false) => {
    setTriggerCount((c) => ({ count: c.count + 1, isManual }));
  }, []);

  const syncStartedRef = useRef(false);
  const foregroundIncrementalCheckedRef = useRef(false);
  const isSyncingRef = useRef(false);
  const isOfflineRef = useRef(false);

  useEffect(() => {
    let isCancelled = false;
    let activeController: AbortController | null = null;
    let appStateHandle: any = null;

    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const fetchSyncPage = (
      page: number,
      lastSyncDate: string | null,
      signal: AbortSignal,
    ) =>
      new Promise((resolve, reject) => {
        signal.addEventListener("abort", reject, { once: true });
        console.log(`[UserSync] Calling SyncEmployeeDataAPI. isOfflineRef: ${isOfflineRef.current}`);
        (dispatch as any)(
          SyncEmployeeDataAPI({
            filters: {
              page,
              limit: PAGE_LIMIT,
              last_sync_date: lastSyncDate || null,
            },
            onSuccess: resolve,
            onError: reject,
            signal,
          }),
        );
      });

    const startSync = async () => {
      if (isSyncingRef.current) return;
      if (isOfflineRef.current) return;

      if (!Capacitor.isNativePlatform()) {
        // Web: there is no local SQLite employee store to sync — the tablet
        // on web matches faces and clocks in/out through the server API.
        syncStartedRef.current = true;
        setIsSyncing(true);
        setSyncProgress(100);
        await Promise.resolve();
        setIsSyncing(false);
        isSyncingRef.current = false;
        return;
      }

      // Check backend health (/health) before calling SyncEmployeeDataAPI (/sync-data)
      const isServerHealthy = await serverHealthService.pingServer();
      if (!isServerHealthy) {
        console.log("[UserSync] Server /health check failed. Skipping /sync-data.");
        serverHealthService.startServerReconnectionPolling();
        return;
      }

      isSyncingRef.current = true;
      if (isCancelled) {
        isSyncingRef.current = false;
        return;
      }

      let sqliteCheckpoint = await userStorage.getLastPage();

      // Retrieve the last sync date to perform an incremental sync
      let lastDate = await userStorage.getSyncMetadata();

      if (triggerCount.isManual) {
        // Force full sync on manual trigger to ensure all data is consistent
        lastDate = null;
        foregroundIncrementalCheckedRef.current = false;
        // Clear local DB to remove stale records not returned by the backend anymore
        await userStorage.clear();
        await userStorage.saveLastPage(0);
        sqliteCheckpoint = 0;
      } else if (lastDate && foregroundIncrementalCheckedRef.current && triggerCount.count === 0) {
        isSyncingRef.current = false;
        window.dispatchEvent(new Event("trigger-attendance-sync"));
        return; // Already checked incremental on this foreground session
      }

      syncStartedRef.current = true;
      setIsSyncing(true);
      setSyncProgress(0);

      let currentPage = sqliteCheckpoint > 0 ? sqliteCheckpoint + 1 : 1;
      let hasMore = true;
      let syncedRows = sqliteCheckpoint * PAGE_LIMIT;
      let finalServerTime: string | null = null;

      console.log(`[UserSync] Sync started. page=${currentPage}, lastDate=${lastDate}`);

      while (hasMore && !isCancelled) {
        if (isOfflineRef.current) break;
        try {
          const controller = new AbortController();
          activeController = controller;

          const res: any = await fetchSyncPage(
            currentPage,
            lastDate,
            controller.signal,
          );
          activeController = null;

          if (isCancelled) break;

          const users = res?.data || [];
          const deletedIds = res?.deleted_ids || [];
          const total = res?.total || 0;
          if (res?.server_time) finalServerTime = res.server_time;

          sessionStorage.removeItem("user_sync_retry_count");

          if (users.length > 0) {
            await userStorage.bulkSave(users);
          }

          // Persist business settings (e.g. faceAccuracyThreshold) so the
          // tablet can use them for local face comparisons without an extra API call.
          if (res?.business_settings && Object.keys(res.business_settings).length > 0) {
            await userStorage.saveBusinessSettings(res.business_settings);
          }


          if (deletedIds.length > 0) {
            await userStorage.deleteUsersByIds(deletedIds);
          }

          const syncedCount = syncedRows + users.length;
          let progress = total > 0 ? (syncedCount / total) * 100 : 100;
          progress = Math.min(100, progress);
          setSyncProgress(progress);

          await userStorage.saveLastPage(currentPage);
          syncedRows = syncedCount;

          const hasNextPage = syncedCount < total;

          if (users.length > 0 && hasNextPage) {
            currentPage++;
            await wait(25);
          } else {
            console.log(`✅ User sync fully completed. Total: ${total}`);
            await Promise.all([
              finalServerTime ? userStorage.saveSyncMetadata(finalServerTime) : Promise.resolve(),
              userStorage.saveLastPage(0),
            ]);
            foregroundIncrementalCheckedRef.current = true;
            attendanceQueueManager.clearAllOptimisticStatuses();
            serverHealthService.markOnline();
            hasMore = false;
            window.dispatchEvent(new CustomEvent("employee-sync-completed"));
          }
        } catch (err) {
          activeController = null;
          if (isCancelled) break;

          const isNetworkOrServerOffline =
            (err as any)?.message === "Network Error" || 
            (err as any)?.message?.includes("Network Error") || 
            (err as any)?.message?.includes("Network Unavailable") || 
            (err as any)?.message?.includes("Cannot read properties of undefined") || 
            ((err as any)?.isAxiosError && !(err as any)?.response);

          // Abort immediately on network or server offline without retrying
          if (isNetworkOrServerOffline) {
            console.warn("⚠️ Server or network offline detected during sync. Aborting user sync.");
            serverHealthService.markOffline();
            serverHealthService.startServerReconnectionPolling();
            hasMore = false;
            break;
          }

          const retryCount = Number(
            sessionStorage.getItem("user_sync_retry_count") || "0",
          );
          if (retryCount < MAX_RETRIES) {
            const nextRetryCount = retryCount + 1;
            const delay = Math.min(30000, 2000 * 2 ** retryCount);
            sessionStorage.setItem("user_sync_retry_count", String(nextRetryCount));
            console.warn(`⚠️ User Sync page ${currentPage} failed. Retrying...`, err);
            await wait(delay);
            if (isOfflineRef.current) break;
            continue;
          }

          sessionStorage.removeItem("user_sync_retry_count");
          console.error("❌ User Sync Engine Error:", err);
          hasMore = false;
        }
      }
      setIsSyncing(false);
      isSyncingRef.current = false;
      window.dispatchEvent(new Event("trigger-attendance-sync"));
    };

    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
        if (isActive) {
          syncStartedRef.current = false;
          foregroundIncrementalCheckedRef.current = false;
          startSync();
        }
      }).then((handle) => {
        appStateHandle = handle;
      });
    }

    const handleExternalTrigger = () => {
      syncStartedRef.current = false;
      foregroundIncrementalCheckedRef.current = false;
      startSync();
    };

    const handleOnline = () => {
      syncStartedRef.current = false;
      foregroundIncrementalCheckedRef.current = false;
      startSync();
    };
    const initializeNetworkCheck = async () => {
      const status = await Network.getStatus();
      isOfflineRef.current = !status.connected;
      if (status.connected) {
        startSync();
      }
    };
    initializeNetworkCheck();

    const networkListener = Network.addListener(
      "networkStatusChange",
      (status) => {
        console.log('[UserSync] Network status changed:', status.connected ? 'Online' : 'Offline', status);
        if (status.connected) {
          isOfflineRef.current = false;
          handleOnline();
        } else {
          isOfflineRef.current = true;
        }
      },
    );

    const timer = setTimeout(() => {
      startSync();
    }, 2000);

    const handleSocketConnect = () => {
      console.log("[UserSync] Socket reconnected to server. Starting employee sync...");
      startSync();
    };

    window.addEventListener("trigger-employee-sync", handleExternalTrigger);
    window.addEventListener("online", handleOnline);

    if (socket) {
      socket.on("connect", handleSocketConnect);
      socket.on("employee-attendance-updated", handleExternalTrigger);
      socket.on("hr:settings-updated", handleExternalTrigger);
      socket.on("employee-auto-sync", handleExternalTrigger);
    }

    return () => {
      isCancelled = true;
      activeController?.abort();
      clearTimeout(timer);
      appStateHandle?.remove?.();
      window.removeEventListener("trigger-employee-sync", handleExternalTrigger);
      window.removeEventListener("online", handleOnline);
      if (networkListener) {
        networkListener.then((listener: any) => listener.remove());
      }
      if (socket) {
        socket.off("connect", handleSocketConnect);
        socket.off("employee-attendance-updated", handleExternalTrigger);
        socket.off("hr:settings-updated", handleExternalTrigger);
        socket.off("employee-auto-sync", handleExternalTrigger);
      }
    };
  }, [dispatch, triggerCount, socket, isConnected]);

  return { syncProgress, isSyncing, triggerSync };
};
